/*
 * Copyright 2022 Benjamin Menant.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    ExpandableSection,
    Level,
    LevelItem,
    Text,
    Title,
} from '@patternfly/react-core';
import {useTranslation} from "react-i18next";
import type {IAMDirectDebitMandateJsonSchema as DDM} from '../../donor-service/types/IAMDirectDebitMandateJsonSchema';
import {FORMAT_DATE_ONLY, formatDate} from "../../utils/formatDate.ts";
import {formatNumber} from "../../utils/formatNumber.ts";
import {useEnvironmentWithDonorService} from "../../context.tsx";

export type DirectDebitMandateProps = {
    mandate: DDM;
}

export function DirectDebitMandate ({
    mandate: {
        amount,
        dateSigned,
        iban,
        mandateId,
        nextDebitDate,
        periodicity,
    },
}: DirectDebitMandateProps) {
    const { t } = useTranslation();
    const context = useEnvironmentWithDonorService();

    const commitmentNumber = String(mandateId)
        .padStart(6, '0')
        .match(/\w{1,3}/g)!.join('\u202f');

    const redactedIban = redactIban(iban);

    const _nextDebitDate = new Date(nextDebitDate);
    const prettyNextDebitDate = isNaN(_nextDebitDate.valueOf())
        ? t("nextDebitDateUnknown")
        : formatDate(_nextDebitDate, context.environment.locale, FORMAT_DATE_ONLY);

    const _dateSigned = new Date(dateSigned || '');
    const prettyDateSigned = isNaN(_dateSigned.valueOf())
        ? undefined
        : formatDate(_dateSigned, context.environment.locale, FORMAT_DATE_ONLY);

    const prettyAmount = formatNumber(amount);

    return (
        <Card className="ape-c-card">
            <CardHeader
                actions={{
                    actions: [<Title key={commitmentNumber} size="xl" headingLevel="h4">№ {commitmentNumber}</Title>],
                    hasNoOffset: true,
                }}
            >
                <CardTitle>
                    <Level hasGutter>
                        <LevelItem><Title size="4xl" headingLevel="h2">🏦</Title></LevelItem>
                        <LevelItem><Title size="xl" headingLevel="h2">Mandat SEPA</Title></LevelItem>
                        <LevelItem>
                            <Title size="xl" headingLevel="h3">
                                <strong>{prettyAmount} {t(periodicity)}</strong>
                            </Title>
                        </LevelItem>
                    </Level>
                </CardTitle>
            </CardHeader>
            <CardBody>
                <Text>{t("iban")} {redactedIban}</Text>
                <Text>{t("dateSigned")} {prettyDateSigned}</Text>
                <Text>{t("nextDebitDate")} {prettyNextDebitDate}</Text>
            </CardBody>
            <CardBody>
                <ExpandableSection isIndented toggleText={t('EditOrSuspend')}>
                    <Text>
                        <Text component="a" href={t('contactPageHref')} target="_blank" rel="noreferrer noopener">
                            {t("doContactUs")}
                        </Text> {t("contactUsToEditOrSuspend", { ref: commitmentNumber })}
                    </Text>
                </ExpandableSection>
            </CardBody>
        </Card>
    )
}

function redactIban (iban: string): string {
    const compact = iban.replace(/\s/g, '');
    const endingChars = compact.length % 4 || 4;
    const start = compact.slice(0, 4);
    const middle = compact.slice(4, -endingChars).replace(/\w/g, 'X');
    const end = compact.slice(-endingChars);
    return `${start}${middle}${end}`.match(/\w{1,4}/g)!.join('\u202f');
}

